import React, { useState, Fragment, useEffect, useRef } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import MainFooter from 'components/Core/Footer/MainFooterComponent/MainFooter';
import MobileInvoicePreviewWrapper from 'components/Core/Invoice/Preview/MobileInvoicePreviewWrapper/MobileInvoicePreviewWrapper';
import Payment from 'components/Core/Payment/Payment';
import DepositPayment from 'components/Deposit/Pending/Payment/DepositPayment';
import Button from 'components/Shared/Button/Button';
import { FeedbackButton } from 'components/Shared/Feedback/Feedback';
import { FrictionlessDetails } from 'components/Shared/FrictionlessCheckout/FrictionlessDetails';
import FrictionlessSignupContainer from 'components/Shared/FrictionlessCheckout/FrictionlessSignupContainer';
import FrictionlessLoginContainer from 'components/Shared/FrictionlessLogin';
import GeneralPageMessage from 'components/Shared/GeneralPageMessage/GeneralPageMessage';
import PaymentAmountInput from 'components/Shared/Inputs/PaymentAmountInput';
import MobileCompanyLogo from 'components/Shared/Merchant/CompanyLogo/MobileCompanyLogo';
import MobileInvoiceHeader from 'components/Shared/MobileInvoiceHeader/MobileInvoiceHeader';
import IdentityErrorMessage from 'components/Shared/PageMessages/IdentityErrorMessage';
import PaymentSection from 'components/Shared/PaymentSection/PaymentSection';
import Separator from 'components/Shared/Separator/Separator';
import SignInButton from 'components/Shared/SignInButton/SignInButton';
import Spinner from 'components/Shared/Spinner/Spinner';
import SubscriptionCancelModal from 'components/Subscription/CancelModal/SubscriptionCancelModal';
import SubscriptionError from 'components/Subscription/Error/SubscriptionError';
import SubscriptionInvoiceHeader from 'components/Subscription/InvoiceHeader/SubscriptionInvoiceHeader';
import SubscriptionPreviewWrapper from 'components/Subscription/PreviewWrapper/SubscriptionPreviewWrapper';
import { useIsMobileScreen } from 'hooks/useIsMobile';
import SegmentIO from 'reporting/SegmentIO';
import WavefrontReporter from 'reporting/wavefront/WavefrontReporter';
import { isFrictionlessUserNameAvailableSelector } from 'store/auth/selectors';
import { signIn, authActions } from 'store/auth/slice';
import { companyInfoSelectors } from 'store/companyInfo/selectors';
import { isFrictionlessPhase2SubscriptionsEnabled } from 'store/ixp/selector';
import { modalActions } from 'store/modal/slice';
import { PAYMENT_REJECT_TYPES, isPaymentError } from 'store/payment/helpers';
import { getUsedPaymentMethod, getUsedPaymentType } from 'store/payment/selectors';
import { paymentActions, paymentAmountChange, paymentAmountReset } from 'store/payment/slice';
import { saleSelectors } from 'store/sale/selectors';
import { updateSubscriptionInfo } from 'store/sale/slice';
import { AppDispatch, RootState } from 'store/store';
import { getUserWalletByEntryId } from 'store/wallet/helpers';
import { walletSelectors } from 'store/wallet/selectors';
import { colors, fontSize, breakpoints, breakpointsNumbers } from 'styles/cp';
import { CompanyInfo } from 'types/CompanyInfo';
import { Config } from 'types/Config';
import { FeatureFlags } from 'types/FeatureFlags';
import { IXP } from 'types/IXP';
import { Payment as PaymentType } from 'types/Payment';
import { Sale, LineItem } from 'types/Sale';
import { PageVariant, CURRENCY, SALE_MAP } from 'types/constants';

const { SUBSCRIPTION_STATUS_TYPES } = SALE_MAP;

const GeneralErrorSUBSCRIPTION_STATUS_TYPES = [
  SUBSCRIPTION_STATUS_TYPES.PAUSED,
  SUBSCRIPTION_STATUS_TYPES.INCOMPLETE_EXPIRED,
  SUBSCRIPTION_STATUS_TYPES.DECLINED,
] as const;
const CanceledSUBSCRIPTION_STATUS_TYPES = [SUBSCRIPTION_STATUS_TYPES.CANCELLED] as const;
const ActiveSubscriptionWithFailedPaymentTypes = [SUBSCRIPTION_STATUS_TYPES.ERROR] as const;

const wavefrontReporter = WavefrontReporter.getInstance();

interface UnifiedPaymentComponentProps {
  pageVariant: PageVariant;
  sale: Sale;
  companyInfo: CompanyInfo;
  config: Config;
  payment: PaymentType;
  featureFlags: FeatureFlags;
  ixp: IXP | {};
  onAccept: () => void;
  onDecline: () => void;
  pdfStatus: {
    downloading: boolean;
    error: boolean;
    url?: string;
  };
}

export const UnifiedPaymentComponent: React.FC<UnifiedPaymentComponentProps> = ({
  pageVariant,
  sale,
  companyInfo,
  config,
  payment,
  featureFlags,
  ixp,
  onAccept,
  onDecline,
  pdfStatus,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const intl = useIntl();

  // Get additional data for subscriptions
  const auth = useSelector((state: RootState) => state.auth);
  const wallet = useSelector((state: RootState) => state.wallet);

  const isDepositPending = pageVariant === PageVariant.DEPOSIT_PENDING;
  const isSubscription = pageVariant === PageVariant.SUBSCRIPTION;

  // Common selectors
  const currency = saleSelectors.currencySelector(sale) || CURRENCY.USD;
  const balanceAmount = saleSelectors.balanceSelector(sale) || 0;
  const estimateAmount = saleSelectors.amountSelector(sale) || 0;
  const depositAmount = saleSelectors.depositAmountSelector(sale) || 0;
  const companyName = companyInfoSelectors.nameSelector(companyInfo) || '';
  const logo = companyInfoSelectors.logoSelector(companyInfo) || null;

  // Subscription-specific selectors and state
  const subscriptionStatus = saleSelectors.subscriptionStatus(sale);
  const subscriptionInfo = saleSelectors.subscriptionInfo(sale);
  const paymentInfo = saleSelectors.subscriptionPaymentInfo(sale);
  const companyLocale =
    companyInfo?.language && companyInfo?.region
      ? `${companyInfo.language}_${companyInfo.region}`
      : 'en_US';
  const region = companyInfo?.region;
  const amount = saleSelectors.amountSelector(sale);
  const totalAmountWithoutOTF = saleSelectors.totalAmountWithoutOTF(sale);
  const taxAmount = sale?.tax?.totalTaxAmount || 0;
  const lineItems = sale?.lines || [];
  const linesWithoutOTF = saleSelectors.linesWithoutOTF(sale);
  const onlyOTFLines = saleSelectors.onlyOTFLines(sale);
  const oneTimeFeeEnabled = sale?.subscriptionInfo?.oneTimeFeeEnabled || false;
  const isUserSignedIn = auth?.isUserSignedIn || false;
  const entityId = auth?.entityId;
  const userWallets = walletSelectors.userWallets(wallet);
  const selectedWalletId = wallet?.selectedWalletId;
  const isFrictionlessRegistrationRequired =
    auth?.frictionlessCheckout?.isRegistrationRequired || false;

  // Subscription-specific state
  const isSubscriptionActive = () => saleSelectors.isSubscriptionActive(sale);
  const [shouldShowManagePayment, setShouldShowManagePayment] = useState(!isSubscriptionActive());
  const [subscriptionErrorType, setSubscriptionErrorType] = useState<'general' | 'canceled' | ''>(
    ''
  );
  const [isAllowedToModifySubscription, setIsAllowedToModifySubscription] = useState(false);
  const [isSubscriptionStatusError, setIsSubscriptionStatusError] = useState(false);

  // Track previous payment for failed payment detection
  const prevPaymentRef = useRef<PaymentType | null>(null);

  // Helper functions
  const getIsAllowedToModifySubscription = (): boolean => {
    return isUserSignedIn && isSubscriptionActive() && paymentInfo?.walletInfo?.id === entityId;
  };

  // Initialize subscription info if needed
  useEffect(() => {
    if (isSubscription && !subscriptionInfo?.startDate) {
      dispatch(
        updateSubscriptionInfo({
          ...subscriptionInfo,
          startDate: new Date().toJSON().slice(0, 10),
          startDateInstant: new Date().getTime().toString(),
        })
      );
    }
  }, []);

  // Initialize subscription state
  useEffect(() => {
    if (!isSubscription || !subscriptionStatus) return;

    const isSubscriptionStatusError = ActiveSubscriptionWithFailedPaymentTypes.includes(
      subscriptionStatus as typeof ActiveSubscriptionWithFailedPaymentTypes[number]
    );
    const subscriptionErrorType = GeneralErrorSUBSCRIPTION_STATUS_TYPES.includes(
      subscriptionStatus as typeof GeneralErrorSUBSCRIPTION_STATUS_TYPES[number]
    )
      ? 'general'
      : CanceledSUBSCRIPTION_STATUS_TYPES.includes(
          subscriptionStatus as typeof CanceledSUBSCRIPTION_STATUS_TYPES[number]
        )
      ? 'canceled'
      : '';

    setSubscriptionErrorType(subscriptionErrorType);
    setIsAllowedToModifySubscription(getIsAllowedToModifySubscription());
    setIsSubscriptionStatusError(isSubscriptionStatusError);
  }, [subscriptionStatus, isSubscriptionActive]);

  // Check for subscription payment errors and manage payment visibility
  useEffect(() => {
    if (!isSubscription || !(isSubscriptionStatusError || userWallets)) return;

    const isWalletNotFound =
      userWallets &&
      userWallets.findIndex((w) => saleSelectors.isWalletUsedForActiveSubscription(sale, w)) < 0;

    const shouldShowManagePayment =
      isSubscriptionStatusError || (isAllowedToModifySubscription && isWalletNotFound);

    if (shouldShowManagePayment) {
      setShouldShowManagePayment(true);
    }
  }, [isSubscriptionStatusError, userWallets, isAllowedToModifySubscription]);

  // Check if active and different method selected
  useEffect(() => {
    if (isSubscription && isUserSignedIn && isSubscriptionActive() && selectedWalletId) {
      const currentlySelectedWallet = getUserWalletByEntryId(userWallets, selectedWalletId);
      let isSelectedSameAsPayment: boolean = false;
      if (currentlySelectedWallet) {
        const walletUsed = saleSelectors.isWalletUsedForActiveSubscription(
          sale,
          currentlySelectedWallet
        );
        isSelectedSameAsPayment = walletUsed === true;
      }
      dispatch(paymentActions.setSubscriptionUpdateRequired(!isSelectedSameAsPayment));
    }
  }, [selectedWalletId, isUserSignedIn, isSubscriptionActive, userWallets]);

  // Set auto-pay and disable partial/over pay for subscriptions
  useEffect(() => {
    if (isSubscription) {
      dispatch(paymentActions.setIsAutoPayOn(true));
      dispatch(paymentActions.disablePartialAndOverPay(true));
    }
  }, []);

  // Track failed payments for Wavefront reporting
  useEffect(() => {
    if (
      isPaymentError({
        prevPayment: prevPaymentRef.current,
        currentPayment: payment,
      })
    ) {
      wavefrontReporter.trackPayment({
        paymentMethod: getUsedPaymentMethod({ payment: payment } as any),
        status: 'failed',
        subFlow: getUsedPaymentType({ payment: payment } as any),
        isPaymentUsingWallet: walletSelectors.isPaymentUsingWallet({
          wallet,
          payment,
        } as RootState),
      });
    }

    // Update the previous payment reference
    prevPaymentRef.current = payment;
  }, [payment, wallet]);
  const isFrictionlessPhase2Enabled = isFrictionlessPhase2SubscriptionsEnabled({
    featureFlags,
    ixp,
  });
  const paymentRejectType = payment?.paymentRejectType;
  const isEmailExists = !isFrictionlessUserNameAvailableSelector({ auth });
  // Base conditions for both FL phases (1 & 2)
  const baseConditions =
    subscriptionErrorType !== 'canceled' &&
    subscriptionErrorType !== 'general' &&
    !isSubscriptionActive();

  // Phase 1: FrictionlessLoginContainer (modal)
  const shouldShowFrictionlessPhase1 =
    baseConditions &&
    ((isFrictionlessPhase2Enabled && !isUserSignedIn && isEmailExists) ||
      !isFrictionlessPhase2Enabled);

  const shouldShowPaymentForm =
    isUserSignedIn || (isFrictionlessPhase2Enabled && !isUserSignedIn && !isSubscriptionActive());
  // Phase 2: FrictionlessDetails (section)
  // TODO: Temporary - will be controlled by experiment after we define it
  const shouldShowFrictionlessPhase2 =
    baseConditions && isFrictionlessPhase2Enabled && !isUserSignedIn;

  const shouldShowManageButtons =
    !shouldShowManagePayment &&
    isAllowedToModifySubscription &&
    isUserSignedIn &&
    isSubscriptionActive();

  const isMissingConsent = paymentRejectType === PAYMENT_REJECT_TYPES.MISSING_CONSENT;
  const country = companyLocale?.split('_')[1];
  const shouldShowPayButton = isSubscription
    ? isSubscriptionActive()
      ? isAllowedToModifySubscription && !!payment?.isSubscriptionUpdatePaymentMethodRequired
      : !isSubscriptionActive()
    : true;

  // Handlers for subscriptions
  const cancelSubscriptionHandler = () => {
    if (!isSubscription) return;

    SegmentIO.transactionEngaged({
      ui_object: 'link',
      ui_object_detail: 'open_cancel_service_modal',
      ui_action: 'clicked',
      ui_access_point: 'transaction_flow',
    });
    dispatch(
      modalActions.show({
        component: () => <SubscriptionCancelModal />,
        componentProps: {
          closeOnClickOutSide: false,
          closeOnEsc: true,
          showCloseButton: false,
        },
      })
    );
  };

  const onSignInClicked = () => {
    if (!isSubscription) return;

    SegmentIO.transactionEngaged({
      ui_object: 'button',
      ui_object_detail: 'subscription_sign_in',
      ui_action: 'clicked',
      ui_access_point: 'transaction_flow',
      activityType: 'sign_in',
    });
    dispatch(signIn());
  };

  // Helper function to adapt Payment object for components
  const adaptPaymentForComponents = (payment: PaymentType) => ({
    partialPaymentEnabled: payment.partialPaymentEnabled || false,
    inputAmount: payment.inputAmount || 0,
    defaultInputAmount: payment.defaultInputAmount || 0,
    paymentStatus: payment.paymentStatus ? String(payment.paymentStatus) : null,
    isAmountValid: payment.isAmountValid ?? true,
    paymentMethodType: payment.paymentMethodType || '',
    allowOverPay: payment.allowOverPay ?? false,
    isDateScheduled: payment.isDateScheduled ?? false,
    isAutoPayOn: payment.isAutoPayOn ?? false,
  });

  // Mobile-specific state
  const [isAcceptedState, setIsAcceptedState] = useState(false);
  const isMobile = useIsMobileScreen(breakpointsNumbers.md);

  const acceptClickHandler = () => {
    setIsAcceptedState(true);
    onAccept();
  };

  // Handle subscription rendering
  if (isSubscription) {
    // Subscription preview data component
    const SubscriptionPreviewDataComponent = (
      <SubscriptionPreviewWrapper
        lineItems={lineItems as any}
        amount={amount}
        taxAmount={taxAmount}
        currency={currency}
        isSubscriptionError={!!subscriptionErrorType}
        linesWithoutOTF={linesWithoutOTF as unknown as LineItem[]}
        onlyOTFLines={onlyOTFLines as unknown as LineItem[]}
      />
    );

    return (
      <Fragment>
        {/* Error messages are now handled by the parent component (unified.page.tsx) */}
        {isMissingConsent && <GeneralPageMessage text="SUBSCRIPTION_MISSING_CONSENT" />}
        {auth?.frictionlessCheckout?.showIdentityError && <IdentityErrorMessage />}

        {/* Mobile invoice header and preview */}
        <div>
          <MobileInvoiceHeader companyLogoUrl={logo || ''} companyName={companyName} />
          <MobileInvoicePreviewWrapper>
            {SubscriptionPreviewDataComponent}
          </MobileInvoicePreviewWrapper>
        </div>

        {/* Main subscription content */}
        <div className="main-card-wrapper bottom">
          {subscriptionErrorType ? (
            <SubscriptionError companyName={companyName} errorType={subscriptionErrorType} />
          ) : (
            <div>
              <SubscriptionInvoiceHeader
                isSubscriptionActive={isSubscriptionActive()}
                subscriptionInfo={subscriptionInfo}
                isOneTimeFee={oneTimeFeeEnabled}
                totalAmountWithoutOTF={totalAmountWithoutOTF}
                isSignedIn={isUserSignedIn && isAllowedToModifySubscription}
                currency={currency}
                payment={payment}
                balanceAmount={String(balanceAmount)}
                featureFlags={featureFlags}
                paymentAmountReset={() => {}}
                paymentAmountChange={() => {}}
                activeSubscriptionPaymentMethod={
                  isSubscriptionActive() ? paymentInfo?.paymentMaskedCard : false
                }
                region={region}
              />
              <Separator height={25} />
              {shouldShowFrictionlessPhase2 && (
                <FrictionlessDetails
                  isSubscription={true}
                  updateFrictionlessLoginCountry={(country: Record<string, any>) =>
                    dispatch(authActions.updateFrictionlessCheckoutCountry(country))
                  }
                  cdn={config.endpoints.cdn}
                />
              )}
              {isFrictionlessRegistrationRequired && (
                <FrictionlessSignupContainer skipFetchWalletsAfterOTP />
              )}
              <Fragment>
                {shouldShowManageButtons && (
                  <span
                    className="manage-sub-btn"
                    style={{ margin: 0 }}
                    onClick={() => {
                      setShouldShowManagePayment((prev) => !prev);
                    }}
                  >
                    <FormattedMessage
                      id="MANAGE_PAYMENT_METHOD"
                      defaultMessage="Manage payment methods"
                    />
                  </span>
                )}
                {shouldShowPaymentForm && (
                  <div className="payment-wrapper">
                    <Payment {...{ shouldShowPayButton }}></Payment>
                  </div>
                )}
              </Fragment>
              {!shouldShowPaymentForm && !isUserSignedIn && (
                <Fragment>
                  <SignInButton onClick={onSignInClicked}>
                    {isSubscriptionActive() ? (
                      <FormattedMessage
                        id="SUBSCRIPTION_SIGN_IN_TO_MANAGE"
                        defaultMessage="sign in to manage subscription"
                      />
                    ) : (
                      <FormattedMessage
                        id="SIGN_OR_ACCOUNT"
                        defaultMessage="Sign in or create account"
                      />
                    )}
                  </SignInButton>
                </Fragment>
              )}
            </div>
          )}
        </div>

        {/* Footer and additional actions */}
        <Separator height={20} />
        {isAllowedToModifySubscription && (
          <div
            className="manage-sub-btn"
            onClick={cancelSubscriptionHandler}
            data-testid="cancelServiceButton"
          >
            <FormattedMessage
              id="SUBSCRIPTION_CANCEL_ACTION"
              defaultMessage="Cancel service and future payments"
            />
          </div>
        )}
        <MainFooter companyLocale={companyLocale} isPayable={true} />
        <Separator height={20} />

        {/* Feedback button for US users */}
        {country && country.toLowerCase() === 'us' && <FeedbackButton transactionType="INVOICE" />}

        {/* Frictionless login container */}
        {shouldShowFrictionlessPhase1 && (
          <FrictionlessLoginContainer showCloseButton={isFrictionlessPhase2Enabled} />
        )}

        <style jsx>{`
          .main-card-wrapper {
            padding: 15px 66px 0;
            max-width: 882px;

            @media screen and (max-width: 768px) {
              padding: 20px 20px 0;
              &.bottom {
                padding: 0 20px;
              }
            }

            .payment-wrapper {
              display: ${shouldShowManagePayment ? 'block' : 'none'};
            }
          }

          .manage-sub-btn {
            display: block;
            text-decoration: none;
            font-size: ${fontSize.xs};
            color: ${colors.blue};
            text-align: center;
            width: fit-content;
            margin: auto;

            &:hover {
              cursor: pointer;
            }
          }

          .payment-placeholder p {
            margin: 0;
            color: #666;
          }
        `}</style>
      </Fragment>
    );
  }

  // Mobile-specific PDF viewing logic
  const renderViewEstimateMessage = () => {
    if (pdfStatus.url) {
      return (
        <>
          <a href={pdfStatus.url} rel="noopener nofollow">
            <FormattedMessage id="VIEW_ESTIMATE_TEXT" defaultMessage="View estimate" />
          </a>
          <style jsx>{`
            a {
              color: ${colors.intuit_blue};
              font-size: ${fontSize.xs};
              text-decoration: none;
            }
          `}</style>
        </>
      );
    }

    if (pdfStatus.error) {
      return (
        <div aria-label="Estimate unavailable">
          <FormattedMessage id="ESTIMATE_UNAVAILABLE" defaultMessage="Estimate unavailable" />
          <style jsx>{`
            div {
              color: ${colors.whiteGray};
              font-size: ${fontSize.xs};
            }
          `}</style>
        </div>
      );
    }

    return (
      <div>
        <Spinner
          color={colors.whiteGray}
          label={''}
          bgStrokeWidth={2.5}
          width={10}
          strokeWidth={5}
          bgColor={colors.gray08}
        />
        <FormattedMessage id="LOADING_ESTIMATE" defaultMessage="Loading estimate" />
        <style jsx>{`
          div {
            color: ${colors.whiteGray};
            font-size: ${fontSize.xs};
          }
        `}</style>
      </div>
    );
  };

  const renderHeader = () => {
    if (!isDepositPending) return null;

    return (
      <>
        <div className="header">
          <div className="title">
            <FormattedMessage
              id="DEPOSIT_TITLE"
              defaultMessage="Please pay the deposit to start working with this business."
            />
          </div>
          <div className="sub-title">
            <FormattedMessage id="DEPOSIT_SUB_TITLE" defaultMessage="Estimate Total" />{' '}
            <FormattedNumber value={estimateAmount} style="currency" currency={currency} />
          </div>
        </div>
        <style jsx>{`
          .header {
            padding: 15px 66px;

            .title {
              text-align: left;
              font-size: ${fontSize.md};
              color: ${colors.gray};
              font-weight: 600;
            }
            .sub-title {
              text-align: left;
              font-size: ${fontSize.ms};
              margin-top: 8px;
              color: ${colors.gray};
              font-weight: 400;
            }
          }
        `}</style>
      </>
    );
  };

  // Unified rendering for deposit pending - using CSS for responsive design
  if (isDepositPending && isMobile) {
    return (
      <div className="deposit-pending-container">
        <div className="mobile-layout">
          {!isAcceptedState ? (
            <Fragment>
              {logo && <MobileCompanyLogo useRedesign={false} companyLogoUrl={logo} />}
              <span className="amount-label estimate-total">
                <FormattedMessage id="ESTIMATE_TOTAL" defaultMessage="Estimate Total" />
              </span>
              <span className="estimate-amount">
                <FormattedNumber value={estimateAmount} style="currency" currency={currency} />
              </span>
              <div>{renderViewEstimateMessage()}</div>
              <div className="mobile-margins deposit-message">
                <FormattedMessage
                  id="DEPOSIT_INTRO_MESSAGE"
                  defaultMessage="{companyName} requires a deposit when you accept their estimate."
                  values={{ companyName }}
                />
              </div>
              <span className="amount-label">
                <FormattedMessage id="DEPOSIT_AMOUNT" defaultMessage="Deposit Amount" />
              </span>
              <span className="deposit-amount">
                <PaymentAmountInput
                  paymentAmountChange={(newAmount: number) =>
                    dispatch(paymentAmountChange(newAmount))
                  }
                  paymentAmountReset={() => dispatch(paymentAmountReset())}
                  currency={currency}
                  balanceAmount={String(depositAmount)}
                  payment={{
                    ...payment,
                    partialPaymentEnabled: isDepositPending
                      ? true
                      : payment.partialPaymentEnabled || false,
                    isDateScheduled: payment.isDateScheduled ?? false,
                    isAutoPayOn: payment.isAutoPayOn ?? false,
                  }}
                  featureFlags={featureFlags}
                  estimateAmount={estimateAmount}
                  isEstimate={true}
                />
              </span>
              <div className="action-buttons-container">
                <Button
                  aria-label="Accept and pay deposit"
                  disabled={false}
                  buttonType="primary"
                  size="jumbo"
                  width="100%"
                  onClick={acceptClickHandler}
                >
                  <FormattedMessage
                    id="DEPOSIT_ACCEPT_AND_PAY"
                    defaultMessage="Accept and pay deposit"
                  />
                </Button>
                <div className="buttons-spacing" />
                <Button
                  aria-label="Decline"
                  disabled={false}
                  buttonType="tertiary"
                  size="jumbo"
                  width="100%"
                  onClick={onDecline}
                >
                  <FormattedMessage id="ESTIATE_DECLINE" defaultMessage="Decline estimate" />
                </Button>
              </div>
            </Fragment>
          ) : (
            <div className="deposit-container-container">
              <div className="card-wrapper">
                {logo && <MobileCompanyLogo useRedesign={false} companyLogoUrl={logo} />}
                <h2 className="title">{companyName}</h2>
                <div>{renderViewEstimateMessage()}</div>
                <span className="amount-label deposit-amount-label">
                  <FormattedMessage id="DEPOSIT_AMOUNT" defaultMessage="Deposit Amount" />
                </span>
                <span className="mobile-margins deposit-amount">
                  <FormattedNumber value={depositAmount} style="currency" currency={currency} />
                </span>
                <div className="mobile-margins">
                  <DepositPayment />
                </div>
              </div>
            </div>
          )}
        </div>

        <style jsx>{`
          .deposit-pending-container {
            width: 100%;
          }

          /* Mobile Layout Styles */
          .mobile-layout {
            display: block;
            text-align: center;
            @media screen and (min-width: ${breakpoints.md}) {
              display: none;
            }
            h2 {
              margin-top: 4px;
            }
          }

          .mobile-layout .card-wrapper {
            background-color: ${colors.white};
            border-radius: 4px;
            width: 100%;
            box-shadow: 0 1px 1px 0 rgba(188, 187, 187, 0.5);
            display: table;
          }

          .mobile-layout .buttons-spacing {
            height: 16px;
          }

          .mobile-layout .amount-label {
            font-size: ${fontSize.xxs};
            color: ${colors.whiteGray};
            text-transform: uppercase;
            display: block;
            padding-bottom: 8px;

            &.estimate-total {
              padding-bottom: 0;
              margin: 22px 10px 5px;
              padding-top: 20px;
            }
          }
          .mobile-layout .deposit-amount-label {
            margin: 20px 16px 0px;
            text-align: left;
          }

          .mobile-layout .mobile-margins {
            margin: 0 16px;
          }

          .mobile-layout .deposit-message {
            font-size: ${fontSize.xs};
            margin-bottom: 24px;
          }

          .mobile-layout .action-buttons-container {
            margin: 26px 16px;
          }

          .mobile-layout .estimate-amount {
            font-family: AvenirNextforINTUIT-Demi;
            font-size: ${fontSize.md2};
            line-height: 28px;
            color: ${colors.darkGray};
            display: block;
          }

          .mobile-layout .deposit-amount {
            font-family: AvenirNextforINTUIT-Demi;
            font-size: ${fontSize.lg};
            font-weight: 700;
            line-height: 36px;
            color: ${colors.darkGray};
            display: flex;
            justify-content: center;
            height: 100%;
            width: 100%;
          }

          .mobile-layout .deposit-container-container {
            margin-top: ${logo ? '30px' : '0'};
          }

          .mobile-layout .deposit-container-container .deposit-amount {
            text-align: left;
            display: block;
          }
        `}</style>
      </div>
    );
  }

  // Unified return for both deposit pending desktop and payment request
  return (
    <div className="card-wrapper">
      {renderHeader()}
      <PaymentSection
        currency={currency}
        balanceAmount={balanceAmount.toString()}
        payment={adaptPaymentForComponents(payment)}
        featureFlags={featureFlags}
        paymentAmountChange={(newAmount: number) => dispatch(paymentAmountChange(newAmount))}
        paymentAmountReset={() => dispatch(paymentAmountReset())}
        labelText={
          isDepositPending
            ? intl.formatMessage({ id: 'DEPOSIT_AMOUNT', defaultMessage: 'Deposit Amount' })
            : undefined
        }
      />

      {/* Conditional actions for deposit pending */}
      {isDepositPending && (
        <div className="desktop-actions">
          <div className="decline-container">
            <Button
              aria-label="Decline"
              disabled={false}
              buttonType="tertiary"
              size="jumbo"
              width="100%"
              onClick={onDecline}
            >
              <FormattedMessage id="DECLINE" defaultMessage="Decline" />
            </Button>
          </div>
        </div>
      )}

      <style jsx>{`
        .desktop-actions {
          padding: 0 66px;
          text-align: center;
          max-width: 882px;

          .decline-container {
            margin: 8px 0;
          }
        }

        .desktop-payment-disclaimer {
          padding: 0 66px 15px 66px;
        }
      `}</style>
    </div>
  );
};

export default UnifiedPaymentComponent;
